import { db } from './firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import toast from 'react-hot-toast';

/**
 * Get all pending ideas (ideas requiring moderation)
 * @returns {Promise<Array>} - Array of pending ideas
 */
export const getPendingIdeas = async () => {
  try {
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('moderationStatus', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort by creation date (oldest first for FIFO review)
    ideas.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return aTime - bTime;
    });
    
    return ideas;
  } catch (error) {
    console.error('Error getting pending ideas:', error);
    throw new Error('Failed to fetch pending ideas');
  }
};

/**
 * Approve an onboarding idea and grant user full access
 * @param {string} ideaId - The idea ID
 * @param {string} userId - The user ID (idea author)
 * @param {string} adminId - The admin ID performing approval
 * @returns {Promise<void>}
 */
export const approveOnboardingIdea = async (ideaId, userId, adminId) => {
  try {
    // Update the idea to active status
    const ideaRef = doc(db, 'ideas', ideaId);
    await updateDoc(ideaRef, {
      status: 'active',
      moderationStatus: 'approved',
      approvedBy: adminId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // Update the user's onboarding status to approved
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      onboardingStatus: 'approved',
      onboardingApprovedAt: serverTimestamp(),
      onboardingApprovedBy: adminId,
    });
    
    toast.success('Idea approved! User now has full access.');
    
    // TODO: Send approval email to user
    // await sendApprovalEmail(userEmail, userName);
    
  } catch (error) {
    console.error('Error approving idea:', error);
    toast.error('Failed to approve idea. Please try again.');
    throw error;
  }
};

/**
 * Reject an onboarding idea
 * @param {string} ideaId - The idea ID
 * @param {string} adminId - The admin ID performing rejection
 * @param {string} reason - Optional rejection reason
 * @returns {Promise<void>}
 */
export const rejectOnboardingIdea = async (ideaId, adminId, reason = '') => {
  try {
    const ideaRef = doc(db, 'ideas', ideaId);
    await updateDoc(ideaRef, {
      moderationStatus: 'rejected',
      rejectedBy: adminId,
      rejectedAt: serverTimestamp(),
      rejectionReason: reason,
      updatedAt: serverTimestamp(),
    });
    
    toast.success('Idea rejected. User can submit a new idea.');
    
    // TODO: Send rejection email to user
    // await sendRejectionEmail(userEmail, userName, reason);
    
  } catch (error) {
    console.error('Error rejecting idea:', error);
    toast.error('Failed to reject idea. Please try again.');
    throw error;
  }
};

/**
 * Get all users for admin dashboard
 * @returns {Promise<Array>} - Array of users
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    const users = [];
    
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort by creation date (newest first)
    users.sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return bTime - aTime;
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw new Error('Failed to fetch users');
  }
};

/**
 * Get pending users (users who haven't been onboarded yet)
 * @returns {Promise<Array>} - Array of pending users
 */
export const getPendingUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('onboardingStatus', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const users = [];
    
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting pending users:', error);
    throw new Error('Failed to fetch pending users');
  }
};

/**
 * Get onboarding application for a specific user
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>} - The onboarding idea or null if not found
 */
export const getUserOnboardingApplication = async (userId) => {
  try {
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('authorId', '==', userId),
      where('isOnboardingIdea', '==', true),
      where('moderationStatus', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first (and should be only) onboarding idea
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error('Error getting user onboarding application:', error);
    throw new Error('Failed to fetch onboarding application');
  }
};

/**
 * Approve a user directly (without going through idea approval)
 * @param {string} userId - The user ID
 * @param {string} adminId - The admin ID performing approval
 * @returns {Promise<void>}
 */
export const approveUser = async (userId, adminId) => {
  try {
    // Update the user's onboarding status to approved
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      onboardingStatus: 'approved',
      onboardingApprovedAt: serverTimestamp(),
      onboardingApprovedBy: adminId,
    });
    
    toast.success('User approved! They now have full access.');
    
  } catch (error) {
    console.error('Error approving user:', error);
    toast.error('Failed to approve user. Please try again.');
    throw error;
  }
};

/**
 * Send approval email notification (placeholder for future implementation)
 * @param {string} userEmail - User's email
 * @param {string} userName - User's display name
 */
export const sendApprovalEmail = async (userEmail, userName) => {
  // TODO: Implement with EmailJS or Firebase Functions
  console.log(`Sending approval email to ${userEmail} (${userName})`);
  // Example with EmailJS:
  // await emailjs.send('service_id', 'template_id', {
  //   to_email: userEmail,
  //   to_name: userName,
  //   subject: 'Welcome to EVC - Your Account is Approved!',
  //   message: 'Congratulations! Your first idea has been approved...'
  // });
};

/**
 * Send rejection email notification (placeholder for future implementation)
 * @param {string} userEmail - User's email
 * @param {string} userName - User's display name
 * @param {string} reason - Rejection reason
 */
export const sendRejectionEmail = async (userEmail, userName, reason) => {
  // TODO: Implement with EmailJS or Firebase Functions
  console.log(`Sending rejection email to ${userEmail} (${userName}): ${reason}`);
  // Example with EmailJS:
  // await emailjs.send('service_id', 'template_id', {
  //   to_email: userEmail,
  //   to_name: userName,
  //   subject: 'EVC Idea Review - Revision Needed',
  //   message: `Your idea needs some revisions. Reason: ${reason}...`
  // });
};

/**
 * Get all unverified resolutions (resolved ideas pending verification)
 * @returns {Promise<Array>} - Array of unverified ideas
 */
export const getUnverifiedResolutions = async () => {
  try {
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('status', '==', 'resolved'),
      where('verificationStatus', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort by resolution date (oldest first for FIFO review)
    ideas.sort((a, b) => {
      const aTime = a.resolvedAt?.toMillis?.() || 0;
      const bTime = b.resolvedAt?.toMillis?.() || 0;
      return aTime - bTime;
    });
    
    return ideas;
  } catch (error) {
    console.error('Error getting unverified resolutions:', error);
    throw new Error('Failed to fetch unverified resolutions');
  }
};

/**
 * Verify a resolution (approve ROI claim)
 * @param {string} ideaId - The idea ID
 * @param {string} adminId - The admin ID performing verification
 * @param {string} notes - Optional verification notes
 * @returns {Promise<void>}
 */
export const verifyResolution = async (ideaId, adminId, notes = '') => {
  try {
    const ideaRef = doc(db, 'ideas', ideaId);
    await updateDoc(ideaRef, {
      verificationStatus: 'verified',
      verifiedBy: adminId,
      verifiedAt: serverTimestamp(),
      verificationNotes: notes,
    });
    
    toast.success('Resolution verified! ROI counts for leaderboard.');
    
  } catch (error) {
    console.error('Error verifying resolution:', error);
    toast.error('Failed to verify resolution. Please try again.');
    throw error;
  }
};

/**
 * Dispute a resolution (flag as incorrect)
 * @param {string} ideaId - The idea ID
 * @param {string} adminId - The admin ID performing dispute
 * @param {string} reason - Reason for dispute
 * @param {number} correctedPrice - Optional corrected actual price
 * @param {number} correctedROI - Optional corrected ROI
 * @returns {Promise<void>}
 */
export const disputeResolution = async (ideaId, adminId, reason, correctedPrice = null, correctedROI = null) => {
  try {
    const updateData = {
      verificationStatus: 'disputed',
      verifiedBy: adminId,
      verifiedAt: serverTimestamp(),
      verificationNotes: reason,
      disputeReason: reason,
    };
    
    // If corrections provided, update the actual price and ROI
    if (correctedPrice !== null) {
      updateData.actualPrice = correctedPrice;
    }
    if (correctedROI !== null) {
      updateData.roi = correctedROI;
      updateData.verificationStatus = 'corrected'; // Different status for corrected values
    }
    
    const ideaRef = doc(db, 'ideas', ideaId);
    await updateDoc(ideaRef, updateData);
    
    toast.success(correctedPrice !== null ? 'Resolution corrected.' : 'Resolution disputed.');
    
    // TODO: Send notification to user about dispute
    
  } catch (error) {
    console.error('Error disputing resolution:', error);
    toast.error('Failed to dispute resolution. Please try again.');
    throw error;
  }
};

/**
 * Get settled ideas awaiting resolution
 * @returns {Promise<Array>} - Array of settled ideas
 */
export const getSettledIdeas = async () => {
  try {
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('status', '==', 'settled')
    );
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      const ideaData = doc.data();
      const settledDate = ideaData.settledAt?.toDate?.() || new Date(ideaData.settledAt);
      const now = new Date();
      const daysSettled = Math.floor((now - settledDate) / (1000 * 60 * 60 * 24));
      
      ideas.push({
        id: doc.id,
        ...ideaData,
        daysSettled,
      });
    });
    
    // Sort by most recently settled first
    ideas.sort((a, b) => {
      const aTime = a.settledAt?.toMillis?.() || 0;
      const bTime = b.settledAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
    
    return ideas;
  } catch (error) {
    console.error('Error getting settled ideas:', error);
    throw new Error('Failed to fetch settled ideas');
  }
};

/**
 * Resolve a settled market (admin closes position)
 * @param {string} ideaId - The idea ID
 * @param {string} adminId - The admin ID performing resolution
 * @param {Object} resolution - Resolution data (outcome, actualPrice, roi)
 * @returns {Promise<void>}
 */
export const resolveSettledMarket = async (ideaId, adminId, resolution) => {
  try {
    const ideaRef = doc(db, 'ideas', ideaId);
    await updateDoc(ideaRef, {
      status: 'resolved',
      outcome: resolution.outcome,
      actualPrice: resolution.actualPrice,
      roi: resolution.roi, // Stores ROI as-is: positive for gains, negative for losses
      resolvedAt: serverTimestamp(),
      resolvedBy: adminId,
      verificationStatus: 'verified', // Admin-resolved = automatically verified
      verifiedBy: adminId,
      verifiedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    toast.success('Market resolved and verified!');
    
  } catch (error) {
    console.error('Error resolving settled market:', error);
    toast.error('Failed to resolve market. Please try again.');
    throw error;
  }
};

